import { createDrumEventMap } from '@arpeggi/audio'
import { BeatResolution, GenesisSampleType, MAX_LOOP_COUNT, StemType } from '@arpeggi/shared'
import { Instance, onPatch, types } from 'mobx-state-tree'
import ArpRegistry from '../../../../features/arp-registry/ArpRegistry'
import { ForDirectExtend } from '../../../../features/mobx'
import { SongInternal } from '../../../client/genesis/genesis_proto_pb'
import { Arp2Utils } from '../../../client/SongUtils'
import { SongGenerator } from '../../../generator/SongGenerator'
import { DrumLoopStore } from '../loop/DrumLoopStore'
import { DrumSubLoopStore } from '../loop/DrumSubLoopStore'
import { LoopRefStore } from '../loop/LoopRefStore'
import { getRandomSampleForCategory } from '../sample/SampleUtils'
import { ISampleWithEffectsStore, SampleWithEffectsStore } from '../sample/SampleWithEffectsStore'
import { StemSettingsStore } from '../StemSettingsStore'
import { StemStore } from '../StemStore'

// Represents a single stem.
export const DrumStemStore = StemStore.named(StemType.DRUMS)
  .props({
    stemType: StemType.DRUMS,
    drumSamples: types.array(SampleWithEffectsStore),
    drumGains: types.array(types.number)
  })
  .views((self) => ({
    // Returns null if no loop at this section
    getDrumSample(drumIndex): number {
      return self.drumSamples[drumIndex].sampleIndex
    },
    getDrumSampleCount() {
      return self.drumSamples.length
    },
    getDrumGain(drumIndex): number {
      return self.drumGains[drumIndex]
    },
    getCurrentSample(): ISampleWithEffectsStore {
      return self.drumSamples.length > 0 ? self.drumSamples[0] : null
    }
  }))
  .actions((self) => {
    let eventMap = createDrumEventMap(self)

    function afterCreate() {
      eventMap = createDrumEventMap(self)
    }
    onPatch(self, (patch) => {
      if (patch.path.includes('notes') || patch.path.includes('arrangement')) {
        eventMap = createDrumEventMap(self)
      }
    })

    function getEventMap() {
      return eventMap
    }
    // Can be called after create() to initialize this as an empty drum stem
    function init(songDisplayLength: number, samples: ISampleWithEffectsStore[]) {
      self.stemSettings = StemSettingsStore.create()
      self.stemSettings.initialize(songDisplayLength)
      self.stemSettings.setStemGain(0.9)
      samples.forEach((sample) => {
        const newSampleWithEffects = SampleWithEffectsStore.create()
        newSampleWithEffects.cloneFrom(sample)
        self.drumSamples.push(newSampleWithEffects)
        self.drumGains.push(0.6)
      })
    }
    function randomizeDrumLoops(generator: SongGenerator, loopNumber: number = null) {
      // Generate new loops + subloops
      // TODO do this intelligently, so you can each drum instrument come in one at a time
      if (loopNumber === null)
        self.loops.forEach((loop) => {
          loop.drumLoop.subLoops.forEach((subLoop, subLoopIndex) => {
            const sample = self.drumSamples[subLoopIndex]
            const category = ArpRegistry.retrieveFromCache(sample.sampleIndex)?.category
            subLoop.randomizeForSampleCategory(generator, category)
          })
        })
      else {
        const loop = self.loops[loopNumber]
        loop.drumLoop.subLoops.forEach((subLoop, subLoopIndex) => {
          const sample = self.drumSamples[subLoopIndex]
          const category = ArpRegistry.retrieveFromCache(sample.sampleIndex)?.category
          subLoop.randomizeForSampleCategory(generator, category)
        })
      }
    }
    function addLoop() {
      if (self.loops.length >= MAX_LOOP_COUNT) return
      const loopRef = LoopRefStore.create()
      const drumLoop = DrumLoopStore.create()
      drumLoop.initialize(Array(self.drumSamples.length).fill(BeatResolution.SIXTEENTH_NOTE))
      loopRef.setDrumLoop(drumLoop)
      self.loops.push(loopRef)
    }

    function clearLoop(loopNumber: number) {
      const loopRef = LoopRefStore.create()
      const drumLoop = DrumLoopStore.create()
      drumLoop.initialize(Array(self.drumSamples.length).fill(BeatResolution.SIXTEENTH_NOTE))
      loopRef.setDrumLoop(drumLoop)
      self.loops[loopNumber] = loopRef
    }

    // Randomizes the loop and sample
    function randomize(generator: SongGenerator) {
      randomizeDrumSamples()
      randomizeDrumLoops(generator)
    }

    // Randomizes a specific loop
    function randomizeLoop(generator: SongGenerator, loopNumber: number) {
      randomizeDrumLoops(generator, loopNumber)
    }

    function randomizeDrumSamples() {
      // Generate new samples, one per subloop
      self.loops[0].drumLoop.subLoops.forEach((_, subLoopIndex) => {
        randomizeDrumSampleForSubloop(subLoopIndex)
      })
    }

    function randomizeDrumSampleForSubloop(subloopIndex: number) {
      const currentSampleIndex = self.drumSamples[subloopIndex].sampleIndex
      const category = ArpRegistry.retrieveFromCache(currentSampleIndex)?.category
      self.drumSamples[subloopIndex] = getRandomSampleForCategory(category)
    }

    function updateDrumSample(sample: ISampleWithEffectsStore, drumIndex) {
      self.drumSamples[drumIndex].cloneFrom(sample)
    }
    function addDrumSample(sample: ISampleWithEffectsStore) {
      const newSample = SampleWithEffectsStore.create()
      newSample.cloneFrom(sample)
      self.drumSamples.push(newSample)
      self.drumGains.push(0.5)
      self.loops.map((loop) => {
        const drumSubLoop = DrumSubLoopStore.create()
        drumSubLoop.initialize(BeatResolution.EIGHTH_NOTE)
        loop.drumLoop.subLoops.push(drumSubLoop)
      })
    }
    function removeDrumSample(sampleIndex: number) {
      self.drumSamples.splice(sampleIndex, 1)
      self.loops.forEach((loop) => {
        loop.drumLoop.subLoops.splice(sampleIndex, 1)
      })
    }
    function updateDrumGain(drumIndex, gain) {
      self.drumGains[drumIndex] = gain
    }
    /** Converts the drum stems from the Genesis proto to a single drum stem on the new proto. */
    function fromGenesisDrum(arp1: SongInternal) {
      // In Genesis, we hard coded these gain multipliers in our audio player to make hat and perc quieter.
      const HAT_VOL: number = 0.6
      const PERC_VOL: number = 0.5

      self.drumGains.push(arp1.getInstrumentGain().getKick() / 15)
      self.drumGains.push(arp1.getInstrumentGain().getSnare() / 15)
      self.drumGains.push((arp1.getInstrumentGain().getHat() * HAT_VOL) / 15)
      self.drumGains.push((arp1.getInstrumentGain().getPerc() * PERC_VOL) / 15)

      self.drumSamples.push(
        Arp2Utils.convertGenesisSampleWithEffectsToV2(
          GenesisSampleType.KICK,
          arp1.getSelectedSamples().getKick()
        )
      )
      self.drumSamples.push(
        Arp2Utils.convertGenesisSampleWithEffectsToV2(
          GenesisSampleType.SNARE,
          arp1.getSelectedSamples().getSnare()
        )
      )
      self.drumSamples.push(
        Arp2Utils.convertGenesisSampleWithEffectsToV2(
          GenesisSampleType.HAT,
          arp1.getSelectedSamples().getHat()
        )
      )
      self.drumSamples.push(
        Arp2Utils.convertGenesisSampleWithEffectsToV2(
          GenesisSampleType.PERC,
          arp1.getSelectedSamples().getPerc()
        )
      )

      self.stemSettings = StemSettingsStore.create()
      self.stemSettings.initialize(16)
      self.stemSettings.setStemGain(1)

      // Need to iterate over every section of the song and potentially create a new loop from the given combination of kick, snare, hat and perc parts.
      const drumLoopMap = new Map<string, number>()
      let nextLoopIndex = 0

      for (let i = 0; i < 16; i++) {
        const kick = arp1.getSongStructure().getKickFlow().getFlowList()[i] - 1
        const snare = arp1.getSongStructure().getSnareFlow().getFlowList()[i] - 1
        const hat = arp1.getSongStructure().getHatFlow().getFlowList()[i] - 1
        const perc = arp1.getSongStructure().getPercFlow().getFlowList()[i] - 1
        const key = kick + '.' + snare + '.' + hat + '.' + perc

        if (kick === 0 && snare === 0 && hat === 0 && perc === 0) {
          self.stemSettings.setArrangement(i, 0)
        } else if (drumLoopMap.has(key)) {
          // This loop combo already exists, don't need to make a new loop
          self.stemSettings.setArrangement(i, drumLoopMap.get(key) + 1)
        } else {
          const drumLoop = DrumLoopStore.create()
          drumLoop.fromGenesis([
            {
              part: Arp2Utils.getPart(kick, arp1.getKickA(), arp1.getKickB(), arp1.getKickC()),
              res: BeatResolution.EIGHTH_NOTE
            },
            {
              part: Arp2Utils.getPart(snare, arp1.getSnareA(), arp1.getSnareB(), arp1.getSnareC()),
              res: BeatResolution.EIGHTH_NOTE
            },
            {
              part: Arp2Utils.getPart(hat, arp1.getHatA(), arp1.getHatB(), arp1.getHatC()),
              res: BeatResolution.SIXTEENTH_NOTE
            },
            {
              part: Arp2Utils.getPart(perc, arp1.getPercA(), arp1.getPercB(), arp1.getPercC()),
              res: BeatResolution.SIXTEENTH_NOTE
            }
          ])
          const loopRef = LoopRefStore.create({ drumLoop: drumLoop })
          self.loops.push(loopRef)
          self.stemSettings.setArrangement(i, nextLoopIndex + 1)
          drumLoopMap.set(key, nextLoopIndex)
          nextLoopIndex++
        }
      }
    }
    return {
      init,
      addDrumSample,
      addLoop,
      getEventMap,
      removeDrumSample,
      randomizeDrumLoops,
      afterCreate,
      updateDrumSample,
      updateDrumGain,
      randomize,
      randomizeLoop,
      randomizeDrumSampleForSubloop,
      fromGenesisDrum,
      clearLoop
    }
  })

export interface IDrumStemStore extends ForDirectExtend<Instance<typeof DrumStemStore>> {}
